import type { CSSProperties, FC } from 'react';
import React from 'react';

import { getNormalizedColSpans } from './../../../../../ricos-common-plugins/layout/get-normalized-col-spans';
import { RicosNodeContextProvider } from './../../../../../ricos-context';
import type { Node } from './../../../../../ricos-schema/legacy/wix/rich_content/v1';
import type { NodeViewRenderer, NodeViewRendererProps } from './../../../../../ricos-types';

import styles from './layout-scss';

const getGridTemplateColumnsTemplate = (node: Node) => {
  const cols: number[] = [];

  node.nodes.forEach(cell => {
    if (cell.type === 'LAYOUT_CELL') {
      cols.push(cell.layoutCellData?.colSpan ?? 0);
    }
  });

  return getNormalizedColSpans(cols)
    .map((col, i) => {
      if (i === 0 || i === cols.length - 1) {
        // To avoid negative margins in viewer we are using a bit of math here. All grid is
        // separated into 12 columns, so we need to calculate the width of each column and subtract
        // the padding from the first and last columns. To make sure viewer and editor looks the
        // same, we need to take into account that editor width is (100% + 2 * horizontalPadding),
        // so we need to calculate the width of each column in editor and subtract the padding from
        // the first and last columns. We keep using `fr` for middle columns in case there are
        // subpixel values - it distributes the remaining space evenly between the columns.
        // One column width is (100 % + 2 * horizontalPadding / 12).
        return `minmax(0, calc(((100% + 2 * var(--ricos-layout-horizontal-padding)) * ${col} / 12) - var(--ricos-layout-horizontal-padding)))`;
      }
      return `minmax(0, ${col}fr)`;
    })
    .join(' ');
};

export const Layout: FC<NodeViewRendererProps> = ({ node, nodeIndex, RicosNodesRenderer }) => {
  return (
    <div
      className={styles.layout}
      style={
        {
          '--ricos-layout-column-template': getGridTemplateColumnsTemplate(node),
        } as CSSProperties
      }
    >
      <RicosNodeContextProvider parentNode={node} parentIndex={nodeIndex}>
        <RicosNodesRenderer key={`children-${node.id}`} nodes={node.nodes ?? []} />
      </RicosNodeContextProvider>
    </div>
  );
};

export const LayoutRenderer: NodeViewRenderer = ({ node, ...props }) => {
  return <Layout key={node.id} node={node} {...props} />;
};
